import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Link2 } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { TID } from "@/lib/testIds";
import StepHeader from "./StepHeader";

export default function LinkStep() {
  const qc = useQueryClient();
  const [npi, setNpi] = useState("");
  const [clientId, setClientId] = useState("");

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => (await api.get("/api/edi/note-to-claim/providers")).data?.providers ?? [],
  });
  const { data: clients = [] } = useQuery({
    queryKey: ["clients"],
    queryFn: async () => (await api.get("/api/edi/note-to-claim/clients")).data?.clients ?? [],
  });

  const linkMut = useMutation({
    mutationFn: async ({ npi_number, client_id }) =>
      (
        await api.patch(`/api/edi/note-to-claim/providers/${npi_number}/client`, {
          client_id,
        })
      ).data,
    onSuccess: () => {
      toast.success("Provider linked to client");
      qc.invalidateQueries({ queryKey: ["providers"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      setNpi(""); setClientId("");
    },
    onError: (e) => toast.error(describeError(e)),
  });

  const linked = useMemo(
    () => providers.filter((p) => p.client_id || p.client_4digit_id),
    [providers],
  );

  const canSubmit = !!npi && !!clientId && !linkMut.isPending;

  return (
    <div className="flex flex-col gap-0 h-full fade-up">
      <StepHeader
        eyebrow="Account · Step 5 of 5"
        title="Link providers to clients"
        subtitle="Tell /edi which client each provider bills under — required before charge capture can route correctly."
        done={linked.length > 0}
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) linkMut.mutate({ npi_number: npi, client_id: clientId });
        }}
        className="mt-6 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end"
      >
        <div className="flex flex-col gap-0.5">
          <label className="lf-label">Provider</label>
          <select
            className="lf-input bg-transparent"
            value={npi}
            data-testid={TID.linkProviderSelect}
            onChange={(e) => setNpi(e.target.value)}
            required
          >
            <option value="">Select a provider…</option>
            {providers.map((p) => (
              <option key={p.id} value={p.npi_number}>
                {p.provider_name} · NPI {p.npi_number}
                {p.client_id || p.client_4digit_id ? "  (currently linked)" : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-0.5">
          <label className="lf-label">Client</label>
          <select
            className="lf-input bg-transparent"
            value={clientId}
            data-testid={TID.linkClientSelect}
            onChange={(e) => setClientId(e.target.value)}
            required
          >
            <option value="">Select a client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.client_id}>
                {c.client_id} · {c.description || "Untitled client"}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          data-testid={TID.linkSubmit}
          className="btn-ink inline-flex items-center gap-1.5"
        >
          {linkMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={14} />}
          Link
        </button>
      </form>

      {providers.length === 0 || clients.length === 0 ? (
        <div className="mt-4 text-[11px] text-[var(--amber)] bg-[var(--amber)]/[.07] border border-[var(--amber)]/30 rounded-[10px] px-3 py-2">
          {providers.length === 0 ? "Add at least one provider first. " : ""}
          {clients.length === 0 ? "Add at least one client first." : ""}
        </div>
      ) : null}

      <div className="mt-7 flex-1">
        <div className="text-[10px] font-bold tracking-[0.1em] uppercase text-[var(--gray2)] pb-2 border-b border-black/[.1] flex items-center justify-between">
          <span>Current links</span>
          <span className="text-[var(--gray3)]">
            {linked.length} / {providers.length} provider{providers.length === 1 ? "" : "s"} linked
          </span>
        </div>

        {providers.length === 0 ? (
          <div className="py-10 text-center text-[12px] text-[var(--gray2)]">
            No providers yet.
          </div>
        ) : (
          <div className="mt-3 rounded-[14px] overflow-hidden border border-black/[.08] bg-white/55">
            {providers.map((p, i) => {
              const cid = p.client_id || p.client_4digit_id;
              const client = clients.find((c) => String(c.client_id) === String(cid));
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 px-4 py-3 ${i ? "border-t border-black/[.06]" : ""}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[var(--ink)] truncate">
                      {p.provider_name}
                    </div>
                    <div className="text-[10.5px] text-[var(--gray2)] mt-0.5 truncate">
                      NPI {p.npi_number}
                    </div>
                  </div>
                  <div className="text-[12px] text-[var(--gray2)]">↔</div>
                  <div className="text-right min-w-[180px]">
                    {cid ? (
                      <>
                        <div className="text-[13px] font-bold text-[var(--ink)]">{cid}</div>
                        <div className="text-[10.5px] text-[var(--gray2)]">
                          {client?.description || "—"}
                        </div>
                      </>
                    ) : (
                      <span className="text-[10px] font-semibold text-[var(--gray2)] bg-black/[.05] border border-black/[.08] px-2 py-0.5 rounded-[6px]">
                        Not linked
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
